import React, { FC, ReactNode } from 'react';
import { Alert, FormGroup } from 'reactstrap';
import { RaffleWithUserDetails } from '../../shared/orders/raffle';
import { useTranslation } from '../translations';
import { Fetcher, useFetcher } from '../utils';
import { ActionButton } from './ActionButton';

interface Props {
  readonly children?: ReactNode;
  readonly raffle: Pick<RaffleWithUserDetails, 'id' | 'roleOverride'>;
}

export const RaffleContainer: FC<Props> = ({ children, raffle }) => {
  const fetcher = useFetcher(async () => await api.getRaffleById(raffle.id));

  if (!fetcher.data) {
    return <Fetcher result={fetcher} />;
  }

  return (
    <RaffleState raffle={fetcher.data} refresh={fetcher.refresh}>
      {children}
    </RaffleState>
  );
};

interface RaffleStateProps {
  readonly children?: ReactNode;
  readonly raffle: RaffleWithUserDetails;
  readonly refresh: () => void;
}

const RaffleState: FC<RaffleStateProps> = ({ children, raffle, refresh }) => {
  const { ts } = useTranslation();
  const ticketStatus = raffle.ticket?.status;
  const hasEntry = ticketStatus === 'waiting';

  if (ticketStatus === 'winner' || ticketStatus === 'redeemed') {
    return (
      <>
        <Alert className="raffle-status-winner" color="success">
          {ts('raffle.you_have_won_in_this')}
        </Alert>
        <br />
        {children}
      </>
    );
  }

  if (ticketStatus === 'rescinded') {
    return (
      <Alert className="raffle-status-rescinded" color="danger">
        {ts('raffle.your_ticket_has_been_rescinded')}
      </Alert>
    );
  }

  if (raffle.status !== 'open') {
    if (hasEntry) {
      return raffle.status === 'closed' ? (
        <Alert className="raffle-status-entered-pending-draw" color="info">
          <p>{ts('raffle.youre_in_the_raffle_for')}</p>
          <p>{ts('raffle.the_raffle_is_now_closed')}</p>
        </Alert>
      ) : (
        <Alert className="raffle-status-lost" color="secondary">
          <p>{ts('raffle.unfortunately_you_didnt_win_in')}</p>
        </Alert>
      );
    }

    return <Alert color="secondary">{ts('raffle.this_raffled_product_is_no')}</Alert>;
  }

  return (
    <FormGroup>
      {hasEntry ? (
        <Alert className="raffle-status-entered" color="primary">
          <p>{ts('raffle.youre_in_the_raffle_for')}</p>
          <p>{ts('raffle.once_the_drawing_takes_place')}</p>
        </Alert>
      ) : (
        <Alert className="raffle-status-not-entered" color="info">
          {ts('raffle.this_is_a_raffled_product')}
        </Alert>
      )}
      <ActionButton
        action={async () => {
          if (hasEntry) {
            await api.leaveRaffle(raffle.id);
          } else {
            await api.enterRaffle(raffle.id);
          }
        }}
        block
        className={`raffle-button-${hasEntry ? 'leave' : 'enter'}`}
        color={hasEntry ? 'danger' : 'primary'}
        id={`raffle${raffle.id}`}
        onSuccess={refresh}
        outline={hasEntry}
      >
        {ts(hasEntry ? 'raffle.leave_raffle' : 'raffle.enter_raffle')}
      </ActionButton>
    </FormGroup>
  );
};
